// assets/offer-cta-otp-only.js
(() => {
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function safeJson(text, fallback) {
    try {
      return JSON.parse(text);
    } catch {
      return fallback;
    }
  }

  function moneyFormatter(currency) {
    const lang = document.documentElement.lang || "en-US";
    try {
      return new Intl.NumberFormat(lang, { style: "currency", currency });
    } catch {
      return new Intl.NumberFormat(lang, { style: "currency", currency: "USD" });
    }
  }

  function fmtMoney(fmt, cents) {
    const n = Number(cents || 0) / 100;
    return fmt.format(n);
  }

  function perDogCents(cents, dogQty) {
    const q = Math.max(1, Number(dogQty || 1));
    if (q <= 1) return Math.round(Number(cents || 0));
    return Math.round(Number(cents || 0) / q);
  }

  // -----------------------------
  // Gallery (thumb rail + arrows)
  // -----------------------------
  function initOfferGallery(root) {
    const gallery = root.querySelector("[data-offer-gallery]");
    if (!gallery) return;

    const stage = gallery.querySelector("[data-offer-stage]");
    const main = gallery.querySelector("[data-offer-main]");
    const viewport = gallery.querySelector("[data-offer-thumb-viewport]");
    const track = gallery.querySelector("[data-offer-thumb-track]");
    const prevBtn = gallery.querySelector("[data-offer-thumb-prev]");
    const nextBtn = gallery.querySelector("[data-offer-thumb-next]");
    const thumbs = Array.from(gallery.querySelectorAll("[data-offer-thumb]"));

    if (!stage || !main || !viewport || !track || !prevBtn || !nextBtn || thumbs.length === 0) return;
    if (gallery.__offerGalleryInit) return;
    gallery.__offerGalleryInit = true;

    const state = { offset: 0, lastMobile: null };

    const isMobile = () => window.matchMedia("(max-width: 749px)").matches;
    const axis = () => (isMobile() ? "x" : "y");
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    function sizeTrack() {
      return axis() === "x" ? track.scrollWidth : track.scrollHeight;
    }
    function sizeViewport() {
      return axis() === "x" ? viewport.clientWidth : viewport.clientHeight;
    }
    function maxOffset() {
      return Math.max(0, sizeTrack() - sizeViewport());
    }

    function apply() {
      const o = clamp(state.offset, 0, maxOffset());
      state.offset = o;

      if (axis() === "x") track.style.transform = `translate3d(${-o}px,0,0)`;
      else track.style.transform = `translate3d(0,${-o}px,0)`;

      prevBtn.disabled = o <= 0;
      nextBtn.disabled = o >= maxOffset() - 1;
    }

    function stepSize() {
      const sample = thumbs[0];
      if (!sample) return 200;
      const r = sample.getBoundingClientRect();
      const one = axis() === "x" ? r.width : r.height;
      const gap = 10;
      return Math.max(1, (one + gap) * 3);
    }

    function scrollBy(dir) {
      state.offset = clamp(state.offset + stepSize() * dir, 0, maxOffset());
      apply();
    }

    prevBtn.addEventListener("click", () => scrollBy(-1));
    nextBtn.addEventListener("click", () => scrollBy(1));

    function setActive(btn) {
      thumbs.forEach((b) => {
        b.classList.remove("is-active");
        b.setAttribute("aria-pressed", "false");
      });
      btn.classList.add("is-active");
      btn.setAttribute("aria-pressed", "true");
    }

    function ensureVisible(btn) {
      const v = viewport.getBoundingClientRect();
      const b = btn.getBoundingClientRect();

      if (axis() === "x") {
        const leftOverflow = v.left - b.left;
        const rightOverflow = b.right - v.right;
        if (leftOverflow > 0) state.offset = clamp(state.offset - leftOverflow, 0, maxOffset());
        else if (rightOverflow > 0) state.offset = clamp(state.offset + rightOverflow, 0, maxOffset());
      } else {
        const topOverflow = v.top - b.top;
        const bottomOverflow = b.bottom - v.bottom;
        if (topOverflow > 0) state.offset = clamp(state.offset - topOverflow, 0, maxOffset());
        else if (bottomOverflow > 0) state.offset = clamp(state.offset + bottomOverflow, 0, maxOffset());
      }

      apply();
    }

    // Lightweight swap (keeps your rail behavior); robust swap is added globally below
    thumbs.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const src = btn.getAttribute("data-offer-src") || btn.getAttribute("data-full");
        if (src) {
          main.src = src;
          main.srcset = src; // important for responsive img
          main.removeAttribute("sizes");
          main.loading = "eager";
        }
        setActive(btn);
        ensureVisible(btn);
      });
    });

    function measureAndConstrain() {
      const mobile = isMobile();
      const rail = gallery.querySelector(".offer-gallery__thumbRail");
      if (!rail) {
        apply();
        return;
      }

      if (mobile) {
        rail.style.removeProperty("height");
        viewport.style.removeProperty("height");
        state.offset = clamp(state.offset, 0, maxOffset());
        apply();
        return;
      }

      const stageH = Math.round(stage.getBoundingClientRect().height);
      if (!stageH) {
        apply();
        return;
      }

      const prevH = prevBtn.getBoundingClientRect().height || 34;
      const nextH = nextBtn.getBoundingClientRect().height || 34;
      const gap = 10;
      const viewportH = Math.max(60, stageH - prevH - nextH - gap - gap);

      rail.style.height = `${stageH}px`;
      viewport.style.height = `${viewportH}px`;

      state.offset = clamp(state.offset, 0, maxOffset());
      apply();
    }

    state.lastMobile = isMobile();
    measureAndConstrain();

    window.addEventListener("resize", () => {
      const nowMobile = isMobile();
      if (nowMobile !== state.lastMobile) {
        state.offset = 0;
        state.lastMobile = nowMobile;
      }
      measureAndConstrain();
    });
  }

  // -----------------------------
  // OTP-only init
  // -----------------------------
  function initOfferCTAOTPOnly(root) {
    initOfferGallery(root);

    if (root.__offerCTAOTPOnlyInit) return;
    root.__offerCTAOTPOnlyInit = true;

    const configEl = $(root, "[data-offer-otp-only-config]");
    if (!configEl) return;

    const config = safeJson(configEl.textContent, null);
    if (!config) return;

    const fmt = moneyFormatter(config.currency);

    const qtyInput = $(root, "[data-offer-qty]");
    const buyNowQtyInput = $(root, "[data-offer-buy-now-qty]");
    const atcBtn = $(root, "[data-offer-atc]");
    const buyNowBtn = $(root, "[data-offer-buy-now]");
    const formEl = root.querySelector("form.offer-cta__productForm");
    const qtyButtons = $$(root, "[data-set-qty]");

    const otpPriceEl = $(root, "[data-offer-otp-price]");
    const otpCompareEl = $(root, "[data-offer-otp-compare]");
    const otpDiscountEl = $(root, "[data-offer-otp-discount]");

    const vv = config.variant || null;
    if (!vv) return;

    let dogQty = 1;
    let offerConfig = null;

    function quoteForQty(qty) {
      return window.RevLiftPricing?.quote
        ? window.RevLiftPricing.quote(offerConfig || {}, [{ id: vv.id, price: vv.price, quantity: qty }], { purchaseOptionMode: 'one_time' })
        : { discountedTotal: Number(vv.price || 0) * qty, discountPercent: 0, activePageOfferIds: [] };
    }

    function syncPageOffers(quote) {
      root.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        ['__page_offer_ids', '_da_page_offer_ids'].forEach(key => {
          let input = form.querySelector(`input[name="properties[${key}]"]`);
          if (!quote.activePageOfferIds.length) { if (input) input.remove(); return; }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${key}]`;
            form.appendChild(input);
          }
          input.value = quote.activePageOfferIds.join(',');
        });
      });
    }

    function loadOffers() {
      if (!window.RevLiftPricing?.fetchConfig) return;
      window.RevLiftPricing.fetchConfig(config.productHandle).then(next => {
        offerConfig = next;
        renderPrices(dogQty);
        root.setAttribute('data-offer-pricing-ready', 'true');
      }).catch(() => { /* Show the undiscounted price if live offers are unavailable. */ });
    }
    document.addEventListener('revlift:pricing-ready', loadOffers, { once: true });
    loadOffers();

    function syncFormQuantityNow() {
      const q = Math.max(1, dogQty);
      if (qtyInput) {
        qtyInput.value = String(q);
        qtyInput.setAttribute("value", qtyInput.value);
      }
      if (buyNowQtyInput) {
        buyNowQtyInput.value = String(q);
        buyNowQtyInput.setAttribute("value", buyNowQtyInput.value);
      }
    }

    function setAvailability() {
      const ok = !!vv.available;
      if (atcBtn) atcBtn.disabled = !ok;
      if (buyNowBtn) buyNowBtn.disabled = !ok;
    }

    function renderPrices(qty) {
      const q = Math.max(1, Number(qty || 1));
      const quote = quoteForQty(q);
      const pct = quote.discountPercent;
      syncPageOffers(quote);
      qtyButtons.forEach(button => {
        const badge = button.querySelector('[data-qty-badge]');
        if (!badge || !/%/.test(badge.textContent)) return;
        const percent = quoteForQty(Number(button.dataset.setQty || 1)).discountPercent;
        badge.textContent = `Save ${percent}%`;
        badge.hidden = percent <= 0;
      });

      const baseCompare = Math.max(Number(vv.price || 0), Number(vv.compare_at_price || 0)) * q;

      const shownTotal = quote.discountedTotal;

      if (otpPriceEl) otpPriceEl.textContent = fmtMoney(fmt, perDogCents(shownTotal, q));

      if (otpCompareEl) {
        if (baseCompare > 0 && baseCompare > shownTotal) {
          otpCompareEl.textContent = fmtMoney(fmt, perDogCents(baseCompare, q));
          otpCompareEl.hidden = false;
        } else {
          otpCompareEl.textContent = "";
          otpCompareEl.hidden = true;
        }
      }

      if (otpDiscountEl) {
        if (pct > 0) {
          otpDiscountEl.textContent = `${pct}% Off`;
          otpDiscountEl.hidden = false;
        } else {
          otpDiscountEl.textContent = "";
          otpDiscountEl.hidden = true;
        }
      }
    }

    function setQty(qty) {
      dogQty = Math.max(1, Number(qty || 1));
      syncFormQuantityNow();

      qtyButtons.forEach((btn) => {
        const isActive = Number(btn.getAttribute("data-set-qty") || "0") === dogQty;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      renderPrices(dogQty);
    }

    qtyButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const qty = Number(btn.getAttribute("data-set-qty") || "1") || 1;
        setQty(qty);
      });
    });

    // Ensure qty correct before Dawn AJAX handler
    formEl?.addEventListener("submit", () => syncFormQuantityNow(), true);
    atcBtn?.addEventListener("click", () => syncFormQuantityNow(), true);

    // BUY NOW: bypass Dawn AJAX and submit the separate form to checkout
    const buyNowFormId = buyNowBtn?.getAttribute("form");
    const buyNowForm = buyNowFormId ? document.getElementById(buyNowFormId) : null;

    if (buyNowBtn && buyNowForm) {
      buyNowBtn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          syncFormQuantityNow();

          const qtyEl = buyNowForm.querySelector('[name="quantity"]');
          if (qtyEl) qtyEl.value = String(Math.max(1, dogQty));
          const idEl = buyNowForm.querySelector('[name="id"]');
          if (idEl) idEl.value = String(vv.id);

          buyNowForm.submit();
        },
        true
      );
    }

    setAvailability();
    setQty(1);
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-offer-cta-otp-only]").forEach(initOfferCTAOTPOnly);
  });

  document.addEventListener("shopify:section:load", (e) => {
    e.target.querySelectorAll?.("[data-offer-cta-otp-only]")?.forEach(initOfferCTAOTPOnly);
  });
})();

// --- FINAL FIX: thumbnail click swaps main image correctly (src + srcset) ---
// (bind once globally so it also works if markup differs or is re-rendered)
if (!window.__offerThumbSwapBound) {
  window.__offerThumbSwapBound = true;

  document.addEventListener("click", function (e) {
    const thumb = e.target.closest(".offer-gallery__thumb, [data-offer-thumb]");
    if (!thumb) return;

    const gallery = thumb.closest(".offer-gallery, [data-offer-gallery]");
    if (!gallery) return;

    const mainImg =
      gallery.querySelector(".offer-gallery__main") ||
      gallery.querySelector("[data-offer-main]");

    if (!mainImg) return;

    const newSrc =
      thumb.getAttribute("data-full") ||
      thumb.getAttribute("data-offer-src");

    if (!newSrc) return;

    e.preventDefault();

    // fully replace image source
    mainImg.src = newSrc;
    mainImg.srcset = newSrc;
    mainImg.removeAttribute("sizes");
    mainImg.loading = "eager";

    // Active state
    gallery
      .querySelectorAll(".offer-gallery__thumb, [data-offer-thumb]")
      .forEach((t) => {
        t.classList.remove("is-active");
        t.setAttribute("aria-pressed", "false");
      });

    thumb.classList.add("is-active");
    thumb.setAttribute("aria-pressed", "true");
  });
}
